import { motion } from "framer-motion";
import { Landmark, Languages } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";
import { ParallaxImage, EASE } from "@/components/Fx";

const PHOTOS = {
  mohammed: "/mohammed.webp",
  sana: "/sana.webp",
  wissam: "/wissam.webp",
};

function Medallion({ f }) {
  return (
    <ParallaxImage
      src={PHOTOS[f.id]}
      alt={f.name}
      className="aspect-[3/4] rounded-t-[9rem] rounded-b-2xl"
      hoverScale={1.1}
    />
  );
}

export default function Team() {
  const { t } = useLang();

  return (
    <main>
      <section className="relative overflow-hidden bg-night" data-testid="team-hero">
        <div className="absolute inset-0">
          <img
            src="/team-walk.webp"
            alt=""
            className="img-warm h-full w-full object-cover opacity-30"
            data-testid="team-hero-bg"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-night/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-44 lg:px-10 lg:pb-28 lg:pt-48">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
            className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-gold"
            data-testid="team-eyebrow"
          >
            <span className="inline-block h-[2px] w-8 bg-terra" />
            {t.team.eyebrow}
          </motion.p>
          <span className="mt-6 block max-w-3xl overflow-hidden">
            <motion.h1
              className="font-serif text-5xl font-medium leading-[1.08] tracking-tight text-sand sm:text-6xl"
              initial={{ y: "112%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.15, ease: EASE, delay: 0.35 }}
              data-testid="team-title"
            >
              {t.team.title}
            </motion.h1>
          </span>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.8 }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-sand/80 md:text-lg"
            data-testid="team-sub"
          >
            {t.team.sub}
          </motion.p>
        </div>
      </section>

      <section className="bg-sand" data-testid="team-banner">
        <div className="mx-auto max-w-7xl px-5 pt-16 lg:px-10">
          <ParallaxImage
            src="/team-desk.webp"
            alt="I tre soci di SWM Consulting — Mohammed Hammouch, Sana Ed Dami e Wissam El Aissaoui — al lavoro con le bandiere di Italia e Marocco"
            className="aspect-[4/3] rounded-2xl lg:aspect-[21/9]"
            hoverScale={1.06}
          />
        </div>
      </section>

      <section className="bg-sand" data-testid="team-profiles">
        <div className="mx-auto max-w-7xl space-y-24 px-5 py-24 lg:px-10 lg:space-y-32 lg:py-32">
          {t.team.founders.map((f, i) => {
            const civic = f.civic.filter((c) => !c.includes("RNI"));
            return (
              <Reveal key={f.id}>
                <article
                  className="grid gap-10 lg:grid-cols-12"
                  data-testid={`founder-card-${f.id}`}
                >
                  <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                    <Medallion f={f} />
                  </div>

                  <div className={`lg:col-span-8 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                    <p className="font-mono text-xs text-terra">0{i + 1}</p>
                    <h2 className="mt-3 font-serif text-3xl font-medium tracking-tight text-night md:text-4xl">
                      {f.name}
                    </h2>
                    <p className="mt-6 max-w-3xl text-base leading-relaxed text-mutedx md:text-lg">
                      {f.bio}
                    </p>

                    <div className="mt-10 rounded-2xl border border-linec bg-white p-6 md:p-8">
                      <p className="flex items-center gap-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-terra">
                        <Landmark className="h-4 w-4" />
                        {t.team.labels.civic}
                      </p>
                      <ul className="mt-4 grid gap-2.5 md:grid-cols-2 md:gap-x-8">
                        {civic.map((c, ci) => (
                          <li
                            key={ci}
                            className="flex items-start gap-2.5 text-sm leading-relaxed text-night/85"
                          >
                            <span className="mt-[7px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-terra" />
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-2.5">
                      <span className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-mutedx">
                        <Languages className="h-4 w-4 text-terra" />
                        {t.team.labels.languages}:
                      </span>
                      {f.languages.map((l, li) => (
                        <span
                          key={li}
                          className="rounded-full border border-linec bg-white px-3.5 py-1.5 text-xs font-semibold text-night"
                        >
                          {l}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>
    </main>
  );
}
